<template>
  <div class="km-withdraw">
    <!-- ===== TOP BAR ===== -->
    <header class="kw-top">
      <button class="kw-back" @click="back" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="kw-title">
        <span class="kw-dot"></span>
        <h1>Withdraw</h1>
      </div>
      <button class="kw-ghost" @click="$router.push({ path:'/WithdrawRecord' })" title="History">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 3"/></svg>
      </button>
    </header>

    <main class="kw-main">
      <!-- ===== HERO BALANCE ===== -->
      <section class="kw-section">
        <div class="kw-balance">
          <div class="kw-balance__glow"></div>
          <div class="kw-balance__lbl">Available Balance</div>
          <div class="kw-balance__val">
            <span class="kw-balance__num">{{ formatMoney(userInfo.money) }}</span>
            <span class="kw-balance__unit">PT</span>
          </div>
          <div class="kw-balance__sub">≈ {{ formatMoney(fiatPreview) }} USD · withdraw to crypto</div>
        </div>
      </section>

      <!-- ===== ASSET SELECTOR ===== -->
      <section class="kw-section">
        <div class="kw-sec-head"><h3>1. Select asset</h3></div>
        <div v-if="loadingAssets" class="kw-assets">
          <div v-for="n in 4" :key="'sk'+n" class="kw-asset kw-asset--skel"></div>
        </div>
        <div v-else-if="!assets.length" class="kw-empty">
          <div class="kw-empty__ico">⏳</div>
          <p>No withdrawal assets are configured yet. Please check back soon.</p>
        </div>
        <div v-else class="kw-assets">
          <button
            v-for="a in assets" :key="a.id"
            class="kw-asset" :class="{ active: selected && selected.id === a.id }"
            @click="selectAsset(a)"
          >
            <span class="kw-asset__ico">{{ symbolIcon(a.symbol) }}</span>
            <div class="kw-asset__body">
              <div class="kw-asset__sym">{{ a.symbol }}</div>
              <div class="kw-asset__net">{{ a.network }}</div>
            </div>
            <span v-if="selected && selected.id === a.id" class="kw-asset__check">✓</span>
          </button>
        </div>
      </section>

      <!-- ===== DESTINATION ADDRESS ===== -->
      <section v-if="selected" class="kw-section">
        <div class="kw-sec-head">
          <h3>2. Destination address</h3>
          <button class="kw-link" v-if="savedAddress" @click="useSaved">Use saved</button>
        </div>

        <div class="kw-card">
          <label class="kw-field">
            <span class="kw-field__lbl">{{ selected.symbol }} address</span>
            <div class="kw-addr-row">
              <input
                v-model.trim="address"
                type="text"
                :placeholder="'Paste your ' + selected.symbol + ' address on ' + selected.network"
                spellcheck="false"
                autocorrect="off"
                autocapitalize="off"
              />
              <button class="kw-paste" @click="pasteAddress" type="button">Paste</button>
            </div>
            <span class="kw-field__hint">
              <span v-if="address">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#10b981" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                Address captured — double-check before confirming
              </span>
              <span v-else>The wallet address that will receive your funds</span>
            </span>
          </label>

          <label class="kw-field" v-if="needsMemo">
            <span class="kw-field__lbl">Memo / Tag <span class="kw-req">required</span></span>
            <input v-model.trim="memo" type="text" placeholder="Tag, memo or destination tag"/>
            <span class="kw-field__hint">Some networks ({{ selected.network }}) require this or funds will be lost.</span>
          </label>
        </div>
      </section>

      <!-- ===== AMOUNT ===== -->
      <section v-if="selected" class="kw-section">
        <div class="kw-sec-head"><h3>3. Amount</h3></div>
        <div class="kw-card">
          <div class="kw-amount-box">
            <div class="kw-amount-row">
              <input
                v-model="amount"
                type="number"
                inputmode="decimal"
                placeholder="0.00"
                @input="clampAmount"
              />
              <span class="kw-amount__unit">PT</span>
              <button class="kw-max" @click="useMax">MAX</button>
            </div>
            <div class="kw-amount-conv">
              ≈ <strong>{{ formatCoin(coinPreview) }} {{ selected.symbol }}</strong>
              <span class="kw-amount-conv__sep">·</span>
              ≈ {{ formatMoney(amountInUsd) }} USD
            </div>
          </div>

          <!-- quick %ages -->
          <div class="kw-quicks">
            <button v-for="p in [25,50,75,100]" :key="p"
                    class="kw-quick" :class="{ active: lastQuick === p }"
                    @click="setPercent(p)">
              {{ p }}%
            </button>
          </div>

          <!-- breakdown -->
          <div class="kw-break">
            <div class="kw-break__row">
              <span>Withdraw amount</span>
              <span>{{ formatMoney(amountNum) }} PT</span>
            </div>
            <div class="kw-break__row">
              <span>Network fee</span>
              <span>− {{ formatMoney(networkFee) }} PT</span>
            </div>
            <div class="kw-break__row kw-break__row--total">
              <span>You'll receive</span>
              <strong>{{ formatCoin(receiveInCoin) }} {{ selected.symbol }}</strong>
            </div>
          </div>
        </div>

        <!-- limits -->
        <div class="kw-rules">
          <div class="kw-rules__head">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
            <span>Limits &amp; notes</span>
          </div>
          <ul>
            <li>Per request: <strong>{{ withdrawRole.min || '—' }}</strong> – <strong>{{ withdrawRole.max || '—' }}</strong> PT</li>
            <li>Daily limit: <strong>{{ withdrawRole.num || '—' }}</strong> requests</li>
            <li>Required confirmations: <strong>{{ selected.confirmations || 1 }}</strong> on-chain</li>
            <li>Only send to a <strong>{{ selected.symbol }}</strong> wallet on the <strong>{{ selected.network }}</strong> network.</li>
          </ul>
        </div>
      </section>

      <!-- ===== CONFIRM BUTTON ===== -->
      <section v-if="selected" class="kw-section">
        <button
          class="kw-submit"
          :disabled="!canSubmit || submitting"
          @click="openConfirm"
        >
          <span v-if="submitting"><span class="kw-spin"></span> Processing…</span>
          <span v-else>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style="margin-right:6px;vertical-align:-3px"><path d="M12 3 4 11h5v10h6V11h5L12 3z"/></svg>
            Withdraw {{ amountNum > 0 ? formatMoney(amountNum) + ' PT' : '' }}
          </span>
        </button>
        <p class="kw-footnote">
          Withdrawals are processed within a few minutes after on-chain confirmation. Check history for status.
        </p>
      </section>

      <div class="kw-spacer"></div>
    </main>

    <!-- ===== CONFIRM MODAL ===== -->
    <transition name="kw-fade">
      <div v-if="showConfirm" class="kw-modal" @click.self="showConfirm=false">
        <div class="kw-modal__card">
          <div class="kw-modal__head">
            <h3>Confirm withdrawal</h3>
            <button class="kw-modal__close" @click="showConfirm=false">×</button>
          </div>
          <div class="kw-modal__body">
            <div class="kw-mrow"><span>Asset</span><strong>{{ selected.symbol }} · {{ selected.network }}</strong></div>
            <div class="kw-mrow"><span>To address</span><code class="kw-mono">{{ shortAddr(address) }}</code></div>
            <div v-if="needsMemo" class="kw-mrow"><span>Memo</span><code class="kw-mono">{{ memo }}</code></div>
            <div class="kw-mrow"><span>Amount</span><strong>{{ formatMoney(amountNum) }} PT</strong></div>
            <div class="kw-mrow"><span>Network fee</span><span>− {{ formatMoney(networkFee) }} PT</span></div>
            <div class="kw-mrow kw-mrow--total">
              <span>Receive</span>
              <strong>{{ formatCoin(receiveInCoin) }} {{ selected.symbol }}</strong>
            </div>
          </div>
          <div class="kw-modal__foot">
            <button class="kw-btn-ghost" @click="showConfirm=false">Cancel</button>
            <button class="kw-btn-primary" @click="doWithdraw" :disabled="submitting">
              <span v-if="submitting"><span class="kw-spin"></span> Submitting…</span>
              <span v-else>Confirm &amp; send</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const ICONS = {
  USDT:'₮', BTC:'₿', ETH:'Ξ', BNB:'B', SOL:'◎', XRP:'✕', TRX:'T', DOGE:'Ð'
};
// Networks that require a memo/tag on withdrawal
const MEMO_NETS = /XRP|XLM|EOS|BEP2|MEMO/i;

export default {
  name: 'KmWithdraw',
  data(){
    return {
      userInfo: {},
      withdrawRole: {},
      loadingAssets: true,
      assets: [],
      selected: null,
      address: '',
      memo: '',
      amount: '',
      lastQuick: 0,
      submitting: false,
      showConfirm: false,
      savedAddress: ''
    };
  },
  computed: {
    amountNum(){ return parseFloat(this.amount) || 0; },
    // 1% network fee, capped at 5 PT min / no max — purely UX estimate
    networkFee(){ return Math.max(0, +(this.amountNum * 0.01).toFixed(2)); },
    netPoints(){ return Math.max(0, this.amountNum - this.networkFee); },
    rate(){ return (this.selected && Number(this.selected.rate)) || 1; },
    coinPreview(){ return this.rate > 0 ? this.amountNum / this.rate : 0; },
    receiveInCoin(){ return this.rate > 0 ? this.netPoints / this.rate : 0; },
    amountInUsd(){ return this.amountNum; }, // 1 PT ≈ 1 USD (display only)
    fiatPreview(){ return Number(this.userInfo.money) || 0; },
    needsMemo(){ return this.selected && MEMO_NETS.test(String(this.selected.network || '')); },
    canSubmit(){
      if(!this.selected) return false;
      if(!this.address || this.address.length < 6) return false;
      if(this.needsMemo && !this.memo) return false;
      const n = this.amountNum;
      const min = Number(this.withdrawRole.min) || 0;
      const max = Number(this.withdrawRole.max) || Infinity;
      if(n <= 0 || n > Number(this.userInfo.money || 0)) return false;
      if(n < min || n > max) return false;
      return true;
    }
  },
  methods: {
    back(){ return window.history.back(); },
    formatMoney(v){
      const n = Number(v);
      if(isNaN(n)) return '0.00';
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatCoin(v){
      const n = Number(v);
      if(isNaN(n) || !n) return '0';
      return parseFloat(n.toFixed(8)).toString();
    },
    symbolIcon(sym){ return ICONS[String(sym || '').toUpperCase()] || '💎'; },
    shortAddr(a){
      a = String(a || '');
      if(a.length <= 14) return a;
      return a.slice(0, 8) + '…' + a.slice(-6);
    },
    selectAsset(a){
      this.selected = a;
      this.amount = ''; this.lastQuick = 0;
      // auto-fill saved address if symbols match
      if(this.savedAddress && !this.address){ this.address = this.savedAddress; }
    },
    useSaved(){ if(this.savedAddress) this.address = this.savedAddress; },
    pasteAddress(){
      if(navigator.clipboard && navigator.clipboard.readText){
        navigator.clipboard.readText().then(t => {
          if(t) { this.address = String(t).trim(); this.$toast && this.$toast('Address pasted'); }
        }).catch(() => this.$toast && this.$toast('Allow clipboard access to paste'));
      } else {
        this.$toast && this.$toast('Clipboard not available — paste manually');
      }
    },
    useMax(){
      const bal = Number(this.userInfo.money) || 0;
      const max = Number(this.withdrawRole.max) || Infinity;
      this.amount = String(Math.min(bal, max));
      this.lastQuick = 100;
    },
    setPercent(p){
      const bal = Number(this.userInfo.money) || 0;
      const max = Number(this.withdrawRole.max) || Infinity;
      this.amount = String(Math.min(bal * (p/100), max).toFixed(2));
      this.lastQuick = p;
    },
    clampAmount(){
      const n = parseFloat(this.amount);
      if(isNaN(n)) return;
      const bal = Number(this.userInfo.money) || 0;
      if(n > bal){ this.amount = String(bal); }
      this.lastQuick = 0;
    },
    openConfirm(){
      if(!this.canSubmit) return;
      this.showConfirm = true;
    },
    doWithdraw(){
      if(!this.canSubmit || this.submitting) return;
      this.submitting = true;
      // Preserve existing backend contract: money is the only required field.
      // Extra metadata is sent for future-proof admin review without breaking
      // the old model (unknown keys are ignored).
      this.$http({
        method: 'post',
        url: 'user_set_withdraw',
        data: {
          money:       this.amountNum,
          asset:       this.selected.symbol,
          network:     this.selected.network,
          address:     this.address,
          memo:        this.memo || ''
        }
      }).then(res => {
        this.submitting = false;
        this.showConfirm = false;
        if(res && res.code === 200){
          this.$toast && this.$toast.success ? this.$toast.success(res.msg || 'Withdrawal submitted') : this.$toast(res.msg);
          this.amount = ''; this.lastQuick = 0;
          this.loadUser(); this.loadRole();
        } else {
          this.$toast && this.$toast(res && res.msg || 'Could not submit');
        }
      }).catch(() => { this.submitting = false; this.showConfirm = false; this.$toast && this.$toast('Network error'); });
    },
    loadUser(){
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if(res && res.code === 200) this.userInfo = res.data;
      });
    },
    loadRole(){
      this.$http({ method: 'get', url: 'user_get_withdraw_role' }).then(res => {
        if(res && res.code === 200) this.withdrawRole = res.data;
      });
    },
    loadAssets(){
      this.loadingAssets = true;
      this.$http({ method: 'get', url: 'deposit_methods' }).then(res => {
        this.loadingAssets = false;
        if(res && res.code === 200 && Array.isArray(res.data)){
          this.assets = res.data;
          if(this.assets.length && !this.selected) this.selectAsset(this.assets[0]);
        }
      }).catch(() => { this.loadingAssets = false; });
    },
    loadSavedAddress(){
      this.$http({ method: 'get', url: 'user_get_bank' }).then(res => {
        if(res && res.code === 200 && res.data && res.data.is_bank && res.data.info){
          this.savedAddress = res.data.info.bankid || '';
        }
      });
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.push({ path: '/Login' }); return; }
    this.loadUser();
    this.loadRole();
    this.loadAssets();
    this.loadSavedAddress();
  }
};
</script>

<style lang="less" scoped>
.km-withdraw {
  min-height: 100vh;
  background:
    radial-gradient(800px 400px at -10% 0%, rgba(255,77,141,.08), transparent 60%),
    radial-gradient(700px 400px at 110% 10%, rgba(160,32,240,.08), transparent 60%),
    linear-gradient(180deg, #fdf5f9 0%, #f6eef3 100%);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #2a1a3f;
  padding-bottom: 120px;
}

/* ===== Top ===== */
.kw-top {
  position: sticky; top: 0; z-index: 40;
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 14px 16px;
  background: rgba(253,245,249,.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(160,32,240,.08);
}
.kw-back, .kw-ghost {
  width: 38px; height: 38px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: #fff; border: 1px solid rgba(160,32,240,.12);
  color: #5a4a78; cursor: pointer;
  transition: all .15s;
}
.kw-back:active, .kw-ghost:active { transform: scale(.95); }
.kw-title { display: flex; align-items: center; gap: 8px; flex: 1; justify-content: center; }
.kw-title h1 { margin: 0; font-size: 17px; font-weight: 800; letter-spacing: -.01em; }
.kw-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 0 3px rgba(255,77,141,.18);
}

.kw-main { padding: 16px; }
.kw-section { margin-bottom: 20px; }
.kw-sec-head {
  display: flex; align-items: center; justify-content: space-between;
  margin: 0 4px 10px;
}
.kw-sec-head h3 {
  margin: 0; font-size: 13px; font-weight: 800;
  color: #5a4a78; letter-spacing: .04em; text-transform: uppercase;
}
.kw-link {
  background: none; border: 0; color: #a020f0; font-weight: 700;
  font-size: 12px; cursor: pointer; letter-spacing: .02em;
}

/* ===== Balance hero ===== */
.kw-balance {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #1a1033 0%, #4a1a6e 100%);
  color: #fff; border-radius: 20px; padding: 26px 22px;
  box-shadow: 0 18px 40px rgba(74,26,110,.3);
  text-align: center;
}
.kw-balance__glow {
  position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(circle at 20% 0%, rgba(255,77,141,.45), transparent 55%),
    radial-gradient(circle at 100% 100%, rgba(160,32,240,.45), transparent 55%);
}
.kw-balance__lbl { position: relative; font-size: 12px; opacity: .85; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.kw-balance__val { position: relative; display: flex; align-items: baseline; justify-content: center; gap: 6px; margin-top: 8px; }
.kw-balance__num { font-size: 38px; font-weight: 900; letter-spacing: -.02em; }
.kw-balance__unit { font-size: 14px; opacity: .85; font-weight: 700; }
.kw-balance__sub { position: relative; margin-top: 6px; font-size: 12px; opacity: .8; }

/* ===== Asset selector ===== */
.kw-assets {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px;
}
.kw-asset {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 12px; text-align: left;
  background: #fff; border: 2px solid rgba(160,32,240,.08);
  border-radius: 14px; cursor: pointer;
  font-family: inherit; position: relative;
  transition: all .18s;
}
.kw-asset:hover { transform: translateY(-1px); border-color: rgba(255,77,141,.3); }
.kw-asset.active {
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  border-color: #ff4d8d;
  box-shadow: 0 8px 22px rgba(255,77,141,.18);
}
.kw-asset--skel { height: 62px; background: linear-gradient(90deg,#f3eff6,#ebe3f1,#f3eff6); background-size: 200% 100%; animation: shimmer 1.6s infinite; border-color: transparent; pointer-events: none; }
@keyframes shimmer { 0%{ background-position: 200% 0; } 100%{ background-position: -200% 0; } }
.kw-asset__ico {
  width: 38px; height: 38px; border-radius: 12px; flex: 0 0 38px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 18px; font-weight: 800;
  box-shadow: 0 4px 10px rgba(255,77,141,.25);
}
.kw-asset__body { flex: 1; min-width: 0; }
.kw-asset__sym { font-weight: 800; font-size: 14px; color: #2a1a3f; }
.kw-asset__net { font-size: 11px; color: #8273a0; margin-top: 1px; }
.kw-asset__check {
  position: absolute; top: 8px; right: 8px;
  width: 18px; height: 18px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 11px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}

/* ===== Cards & fields ===== */
.kw-card {
  background: #fff; border: 1px solid rgba(160,32,240,.1);
  border-radius: 16px; padding: 16px;
  box-shadow: 0 4px 14px rgba(30,27,46,.04);
}
.kw-field { display: block; margin-bottom: 14px; }
.kw-field:last-child { margin-bottom: 0; }
.kw-field__lbl { display: block; font-size: 12px; font-weight: 700; color: #5a4a78; margin-bottom: 6px; letter-spacing: .02em; }
.kw-field__hint { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #8273a0; margin-top: 6px; }
.kw-req { display:inline-block; margin-left:6px; padding:1px 6px; font-size:10px; font-weight:700; color:#b91c1c; background:#fee2e2; border-radius:6px; letter-spacing:.03em; }

.kw-addr-row {
  display: flex; gap: 8px; align-items: stretch;
}
.kw-addr-row input {
  flex: 1; min-width: 0; padding: 11px 12px;
  border: 1.5px solid rgba(160,32,240,.15);
  background: #faf7fd; border-radius: 10px;
  font-size: 13px; font-family: 'SF Mono', ui-monospace, monospace;
  color: #2a1a3f;
  transition: border-color .15s, background .15s;
}
.kw-addr-row input:focus { outline: 0; border-color: #ff4d8d; background: #fff; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }
.kw-paste {
  padding: 0 14px; border-radius: 10px;
  border: 1.5px solid rgba(255,77,141,.3);
  background: linear-gradient(135deg, rgba(255,77,141,.08), rgba(160,32,240,.08));
  color: #a020f0; font-weight: 800; font-size: 12px;
  letter-spacing: .05em; cursor: pointer; text-transform: uppercase;
  transition: all .15s;
}
.kw-paste:hover { border-color: #ff4d8d; background: linear-gradient(135deg, rgba(255,77,141,.15), rgba(160,32,240,.15)); }

.kw-field input[type="text"] {
  width: 100%; padding: 11px 12px;
  border: 1.5px solid rgba(160,32,240,.15);
  background: #faf7fd; border-radius: 10px;
  font-size: 13px; color: #2a1a3f;
}
.kw-field input[type="text"]:focus { outline:0; border-color:#ff4d8d; background:#fff; box-shadow: 0 0 0 3px rgba(255,77,141,.1); }

/* ===== Amount ===== */
.kw-amount-box { padding-bottom: 12px; border-bottom: 1px solid rgba(160,32,240,.08); margin-bottom: 12px; }
.kw-amount-row {
  display: flex; align-items: center; gap: 10px;
}
.kw-amount-row input {
  flex: 1; min-width: 0; font-size: 28px; font-weight: 900;
  letter-spacing: -.02em; color: #2a1a3f;
  border: 0; outline: 0; background: transparent; padding: 4px 0;
}
.kw-amount__unit { font-size: 14px; font-weight: 700; color: #8273a0; }
.kw-max {
  padding: 6px 12px; border-radius: 8px;
  border: 1.5px solid rgba(255,77,141,.35);
  background: linear-gradient(135deg, rgba(255,77,141,.1), rgba(160,32,240,.1));
  color: #ff4d8d; font-size: 11px; font-weight: 800;
  letter-spacing: .05em; cursor: pointer;
  transition: all .15s;
}
.kw-max:hover { border-color: #ff4d8d; transform: translateY(-1px); }
.kw-amount-conv {
  margin-top: 4px; font-size: 12px; color: #8273a0;
}
.kw-amount-conv strong { color: #a020f0; }
.kw-amount-conv__sep { margin: 0 8px; opacity: .5; }

.kw-quicks { display: flex; gap: 8px; margin: 12px 0; }
.kw-quick {
  flex: 1; padding: 8px 0; border-radius: 10px;
  border: 1px solid rgba(160,32,240,.12);
  background: #faf7fd; color: #5a4a78;
  font-size: 12px; font-weight: 800; cursor: pointer;
  transition: all .15s;
}
.kw-quick:hover { border-color: rgba(255,77,141,.4); color: #a020f0; }
.kw-quick.active {
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 6px 14px rgba(255,77,141,.25);
}

.kw-break { font-size: 13px; }
.kw-break__row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 7px 0; color: #5a4a78;
}
.kw-break__row--total {
  padding-top: 12px; margin-top: 4px;
  border-top: 1px dashed rgba(160,32,240,.18);
  color: #2a1a3f;
}
.kw-break__row--total strong { color: #a020f0; font-size: 15px; font-weight: 900; }

/* ===== Rules ===== */
.kw-rules {
  margin-top: 12px; padding: 14px 16px;
  background: linear-gradient(135deg, rgba(59,130,246,.06), rgba(99,102,241,.06));
  border: 1px solid rgba(59,130,246,.2); border-radius: 14px;
}
.kw-rules__head {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 800; color: #3b82f6;
  text-transform: uppercase; letter-spacing: .05em;
  margin-bottom: 8px;
}
.kw-rules ul { margin: 0; padding: 0 0 0 18px; }
.kw-rules li { font-size: 12px; color: #5a4a78; line-height: 1.7; }
.kw-rules li strong { color: #2a1a3f; }

/* ===== Submit ===== */
.kw-submit {
  width: 100%; padding: 16px;
  border-radius: 14px; border: 0; font-size: 15px; font-weight: 800;
  color: #fff; cursor: pointer;
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  box-shadow: 0 14px 30px rgba(255,77,141,.35);
  transition: all .2s;
  letter-spacing: .01em;
}
.kw-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 18px 36px rgba(255,77,141,.45); }
.kw-submit:disabled { opacity: .45; cursor: not-allowed; transform: none; box-shadow: none; }
.kw-footnote { margin: 10px 4px 0; font-size: 11.5px; color: #8273a0; text-align: center; line-height: 1.5; }

.kw-spin {
  display: inline-block; width: 14px; height: 14px;
  border-radius: 50%; border: 2px solid rgba(255,255,255,.35);
  border-top-color: #fff; animation: kwSpin .8s linear infinite;
  vertical-align: -2px; margin-right: 6px;
}
@keyframes kwSpin { to { transform: rotate(360deg); } }

/* ===== Empty ===== */
.kw-empty { text-align: center; padding: 28px 14px; color: #8273a0; }
.kw-empty__ico { font-size: 32px; margin-bottom: 6px; }

.kw-spacer { height: 60px; }

/* ===== Modal ===== */
.kw-modal {
  position: fixed; inset: 0; z-index: 60;
  display: flex; align-items: flex-end; justify-content: center;
  background: rgba(26,16,51,.55); backdrop-filter: blur(4px);
}
.kw-modal__card {
  width: 100%; max-width: 440px; margin: 0 auto;
  background: #fff; border-radius: 20px 20px 0 0;
  box-shadow: 0 -10px 40px rgba(26,16,51,.25);
  overflow: hidden;
  animation: kwSlideUp .28s cubic-bezier(.2,.8,.2,1);
}
@keyframes kwSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.kw-modal__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #1a1033 0%, #4a1a6e 100%);
  color: #fff;
}
.kw-modal__head h3 { margin: 0; font-size: 16px; font-weight: 800; }
.kw-modal__close {
  width: 30px; height: 30px; border-radius: 50%;
  border: 0; background: rgba(255,255,255,.15); color: #fff;
  font-size: 20px; cursor: pointer; line-height: 26px;
}
.kw-modal__body { padding: 16px 18px; }
.kw-mrow {
  display: flex; justify-content: space-between; align-items: center;
  padding: 9px 0; font-size: 13px; color: #5a4a78;
  border-bottom: 1px solid rgba(160,32,240,.06);
}
.kw-mrow:last-child { border-bottom: 0; }
.kw-mrow strong { color: #2a1a3f; font-weight: 800; }
.kw-mrow--total { padding-top: 14px; margin-top: 4px; border-top: 1px dashed rgba(160,32,240,.2); border-bottom: 0; color: #2a1a3f; }
.kw-mrow--total strong { color: #a020f0; font-size: 15px; }
.kw-mono { font-family: 'SF Mono', ui-monospace, monospace; font-size: 12px; background: #f6f0fa; padding: 2px 6px; border-radius: 6px; }
.kw-modal__foot {
  display: flex; gap: 10px; padding: 14px 18px;
  border-top: 1px solid rgba(160,32,240,.08);
  background: #fafafd;
}
.kw-btn-ghost, .kw-btn-primary {
  flex: 1; padding: 12px; border-radius: 12px; font-weight: 800; cursor: pointer;
  font-size: 14px; letter-spacing: .01em; transition: all .2s;
}
.kw-btn-ghost { background: #fff; border: 1px solid rgba(160,32,240,.15); color: #5a4a78; }
.kw-btn-ghost:hover { background: #f6f0fa; }
.kw-btn-primary {
  border: 0; color: #fff;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 8px 20px rgba(255,77,141,.3);
}
.kw-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 26px rgba(255,77,141,.4); }
.kw-btn-primary:disabled { opacity: .6; cursor: not-allowed; }

.kw-fade-enter-active, .kw-fade-leave-active { transition: opacity .2s ease; }
.kw-fade-enter, .kw-fade-leave-to { opacity: 0; }

/* Desktop bump */
@media (min-width: 960px) {
  .kw-main { max-width: 620px; margin: 0 auto; padding: 24px; }
  .kw-balance { padding: 32px 24px; }
  .kw-balance__num { font-size: 44px; }
  .kw-assets { grid-template-columns: repeat(4, 1fr); }
}
</style>
